import { createContext, useContext, useState, useEffect } from "react";
import { supabase } from "./supabaseClient";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]             = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // activeTeam: 선택된 팀 객체 전체를 localStorage에 저장
  const [activeTeam, setActiveTeamState] = useState(() => {
    try { return JSON.parse(localStorage.getItem("mu_activeTeam")); }
    catch { return null; }
  });

  const setActiveTeam = (team) => {
    setActiveTeamState(team);
    if (team) localStorage.setItem("mu_activeTeam", JSON.stringify(team));
    else localStorage.removeItem("mu_activeTeam");
  };

  useEffect(() => {
    // 앱 로드 시 현재 세션 확인 (authLoading 해제 전용 — user null 강제 금지)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) setUser(session.user);
      setAuthLoading(false);
    });

    // 로그인/로그아웃 이벤트 구독
    // event 타입을 반드시 체크: 토큰 갱신(TOKEN_REFRESHED) 등으로
    // session이 순간적으로 null이 되더라도 SIGNED_OUT이 아니면 로그아웃하지 않음
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT") {
        // 명시적 로그아웃일 때만 세션 무효화 (다른 계정 로그인 시 이전 팀 정보 차단 포함)
        setUser(null);
        setActiveTeamState(null);
        localStorage.removeItem("mu_activeTeam");
      } else if (session?.user) {
        // SIGNED_IN, TOKEN_REFRESHED, INITIAL_SESSION, USER_UPDATED 등: 세션 있으면 갱신
        setUser(session.user);
      }
      // session이 잠깐 null이어도 SIGNED_OUT이 아니면 기존 user 유지
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    return { user: data?.user ?? null, error };
  };

  const signUp = async (email, password) => {
    const { data, error } = await supabase.auth.signUp({ email, password });
    return { user: data?.user ?? null, error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setActiveTeamState(null);
    localStorage.removeItem("mu_activeTeam");
  };

  return (
    <AuthContext.Provider value={{ user, authLoading, activeTeam, setActiveTeam, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
